/**
 * Handles runtime storage operations and listens for runtime storage changes.
 *
 * @module pageLoadStorage
 */

import { onSessionChange } from './session';
import logging from '@sstdev/lib_logging';

/** @private */
let storage;

/**
 * Initializes the storage object when the session is destroyed.
 *
 * @callback onSessionChange
 * @param {string} type - The type of session change event.
 */
onSessionChange(type => {
    if (type === 'destroySession') {
        storage = null;
        logging.debug('[PAGE LOAD STORAGE] Session destroyed.');
    }
});

export function getPageLoadId() {
    verifyStorage();
    return storage?._id;
}
/**
 * Retrieves a value from the session storage.
 *
 * @function getKey
 * @param {string} key - The key of the value to retrieve.
 * @param {*} defaultValue - The default value to return if the key does not exist.
 * @returns {*} The value associated with the key, or the default value if the key does not exist.
 */
export function getKey(key, defaultValue) {
    verifyStorage();
    return storage[key] ?? defaultValue;
}

/**
 * Sets a value in the session storage.
 *
 * @function setKey
 * @param {string} key - The key of the value to set.
 * @param {*} value - The value to set.
 */
export function setKey(key, value) {
    verifyStorage();
    storage[key] = value;
}

/**
 * Deletes a value from the session storage.
 *
 * @function deleteKey
 * @param {string} key - The key of the value to delete.
 */
export function deleteKey(key) {
    verifyStorage();
    delete storage[key];
}

function verifyStorage() {
    if (storage == null) {
        const _id = new Date().getTime();
        logging.debug(`[PAGE LOAD STORAGE] session created: ${_id}`);
        storage = { _id };
    }
}

/**
 * Private storage object for session data.
 *
 * @private
 * @type {Object}
 */
export const _private = { storage };

export function clear() {
    storage = null;
}
export default {
    getKey,
    setKey,
    deleteKey,
    clear
};
